import * as React from 'react'
import { useSearchParams } from 'react-router-dom'
import { useRouter } from 'next/router'

type NavigateOptions = { replace?: boolean }

const WAS_DEFAULT_PARAMS_SET: Record<string, number> = {}

export function useQueryParams<PARAMS extends Record<string | number | symbol, any>>(
  defaultParams: Partial<PARAMS> = {},
  paramsFromURLSearchParams: (searchParams: URLSearchParams) => PARAMS,
  paramsToURLSearchParams: (params: PARAMS) => URLSearchParams,
): [PARAMS, (set: (params: PARAMS) => PARAMS, navigateOptions?: NavigateOptions) => void] {
  const [searchParams, setSearchParams] = useSearchParams()
  // This useEffect runs on hook mount to set the default parameters on the url.
  // But since this hook can be used at multiple places on the page, we want only
  // the first instance to actually set the url (to avoid strange issues and
  // clogging the history). To do so we keep track in a global variable if the
  // default params were set for the current pathname.
  // That way, only the first hook running on a certain pathname gets to set the default parameters.
  React.useEffect(() => {
    const loc = location.pathname
    if (!WAS_DEFAULT_PARAMS_SET[loc]) {
      setQueryParam((params) => ({ ...defaultParams, ...params }), { replace: true })
    }

    WAS_DEFAULT_PARAMS_SET[loc] = (WAS_DEFAULT_PARAMS_SET[loc] ?? 0) + 1

    return () => {
      WAS_DEFAULT_PARAMS_SET[loc] = Math.max(0, (WAS_DEFAULT_PARAMS_SET[loc] ?? 0) - 1)
    }
  }, [])

  return [{ ...defaultParams, ...paramsFromURLSearchParams(searchParams) }, setQueryParam]

  function setQueryParam(set: (params: PARAMS) => PARAMS, navigateOptions?: NavigateOptions): void {
    const nextParams = set(paramsFromURLSearchParams(new URL(location.href).searchParams))
    const searchParams = paramsToURLSearchParams(nextParams)
    setSearchParams(searchParams, navigateOptions)
  }
}

// Next.js dependant version of useQueryParams. It has the same signature than classic useQueryParams but uses next router internally
type NextRouterNavigateOptions = { shallow?: boolean; scroll?: boolean }

export function useQueryParamsForNext<PARAMS extends Record<string | number | symbol, any>>(
  defaultParams: Partial<PARAMS> = {},
  paramsFromURLSearchParams: (searchParams: URLSearchParams) => PARAMS,
  paramsToURLSearchParams: (params: PARAMS) => URLSearchParams,
): [PARAMS, (set: (params: PARAMS) => PARAMS, navigateOptions?: NavigateOptions & NextRouterNavigateOptions) => void] {
  const router = useRouter()
  const splittedAsPath = router.asPath.split('?')
  const pathname = splittedAsPath[0]
  const searchParams = splittedAsPath[1]

  React.useEffect(() => {
    const loc = router.pathname

    if (!WAS_DEFAULT_PARAMS_SET[loc]) {
      WAS_DEFAULT_PARAMS_SET[loc] = (WAS_DEFAULT_PARAMS_SET[loc] ?? 0) + 1

      setQueryParams(
        (params) => {
          console.log({ ...defaultParams, ...params })
          return { ...defaultParams, ...params }
        },
        { replace: true, shallow: true },
      )
    } else {
      WAS_DEFAULT_PARAMS_SET[loc] = (WAS_DEFAULT_PARAMS_SET[loc] ?? 0) + 1
    }

    return () => {
      WAS_DEFAULT_PARAMS_SET[loc] = Math.max(0, (WAS_DEFAULT_PARAMS_SET[loc] ?? 0) - 1)
    }
  }, [])

  return [{ ...defaultParams, ...paramsFromURLSearchParams(new URLSearchParams(searchParams)) }, setQueryParams]

  function setQueryParams(
    set: (params: PARAMS) => PARAMS,
    { replace = false, scroll = true, shallow = true }: NavigateOptions & NextRouterNavigateOptions = {},
  ): void {
    apply(set(paramsFromURLSearchParams(new URL(location.href).searchParams)), {
      type: replace ? 'replace' : 'push',
      scroll,
      shallow,
    })
  }

  function apply(
    params: PARAMS,
    { scroll, shallow, type }: { shallow?: boolean; scroll?: boolean; type: 'push' | 'replace' },
  ): void {
    router[type](href(params), undefined, { scroll, shallow })
  }

  function href(params: PARAMS): string {
    const searchParams = paramsToURLSearchParams(params).toString()
    return `${pathname}${searchParams ? `?${searchParams}` : ''}`
  }
}
